import React, {useEffect, useRef} from "react"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import am4themesMaterial from "@amcharts/amcharts4/themes/material"
import am4themesDark from "@amcharts/amcharts4/themes/dark"
import am4themesAnimated from "@amcharts/amcharts4/themes/animated"
import {red, green, blue, orange} from "@material-ui/core/colors"
import {makeStyles} from "@material-ui/core/styles"
import {useStore} from "../Store"
import {useTranslation} from "react-i18next"
import "../i18n"
import {upperCamel} from "../common"
import {useMediaQuery} from "@material-ui/core"
import {useTheme} from "../hooks"

const useStyles = makeStyles({
  cssGrid: {
    display: `grid`,
    gridTemplateColumns: `1fr`,
    width: `100%`,
    marginRight: 16,
    zIndex: (props) => (props.zIndex === `barChart` ? 1201 : 0),
  },
})

export default function SimilarBarChart({id, closing}) {
  const chartData = useRef(null)
  const {t} = useTranslation()
  const {
    store: {zIndex, mainTarget, perspective, page, locale, trendList, themeColor, trendSimilarityList},
  } = useStore()
  const theme = useTheme(themeColor)
  const isMobile = useMediaQuery(theme.breakpoints.down(`xs`))
  const classes = useStyles({
    zIndex,
  })

  const disposeChart = () => {
    if (chartData.current) chartData.current.dispose()
    chartData.current = null
  }

  const createChart = () => {
    const chart = am4core.create(id, am4charts.XYChart)
    if (themeColor === `dark`) am4core.useTheme(am4themesDark)
    if (themeColor !== `dark`) am4core.useTheme(am4themesMaterial)
    am4core.useTheme(am4themesAnimated)
    chart.paddingRight = 0
    chart.cursor = new am4charts.XYCursor()
    chart.cursor.behavior = isMobile ? `none` : `panXY`
    chart.cursor.lineX.opacity = 0
    chart.cursor.lineY.opacity = 0
    chart.svgContainer.htmlElement.style.height = `797px`
    chart.paddingRight = 40
    chart.exporting.menu = new am4core.ExportMenu()
    return chart
  }

  const createValueAxis = (chart) => {
    const valueAxis = chart.xAxes.push(new am4charts.ValueAxis())
    valueAxis.renderer.inside = true
    valueAxis.renderer.grid.template.strokeOpacity = 0
    valueAxis.renderer.baseGrid.strokeOpacity = 0
    valueAxis.renderer.grid.template.disabled = true
    valueAxis.renderer.labels.template.disabled = true
    valueAxis.renderer.labels.template.dy = 20
    valueAxis.cursorTooltipEnabled = false
    valueAxis.min = 0
    valueAxis.max = 100
    valueAxis.title.marginTop = 10
    valueAxis.title.text = t(`chartTrend`)
    valueAxis.title.fill = am4core.color(orange.A700)
    valueAxis.title.fontWeight = `bold`
  }

  const createCategoryAxis = (chart) => {
    const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
    categoryAxis.dataFields.category = `label${upperCamel(locale)}`
    categoryAxis.renderer.grid.template.strokeOpacity = 0
    categoryAxis.renderer.minGridDistance = 10
    categoryAxis.renderer.labels.template.dx = -10
    categoryAxis.renderer.minWidth = 150
    categoryAxis.renderer.tooltip.dx = -15
    categoryAxis.renderer.grid.template.disabled = true
  }

  const createColumnSeries = (chart) => {
    chart.colors.list = [am4core.color(red.A400), am4core.color(green.A700), am4core.color(blue.A700)]
    const columnSeries = chart.series.push(new am4charts.ColumnSeries())
    columnSeries.dataFields.valueX = `similarity`
    columnSeries.dataFields.categoryY = `label${upperCamel(locale)}`
    columnSeries.dataFields.categoryX = `trendType`
    columnSeries.tooltipText = `{valueX.value}%`
    columnSeries.tooltip.pointerOrientation = `vertical`
    columnSeries.tooltip.dy = -10
    columnSeries.columnsContainer.zIndex = 100
    columnSeries.columns.template.adapter.add(`fill`, (fill, target) => {
      if (target.dataItem.categoryX === `UP`) return chart.colors.getIndex(0)
      if (target.dataItem.categoryX === `DOWN`) return chart.colors.getIndex(1)
      if (target.dataItem.categoryX === `NEUTRAL`) return chart.colors.getIndex(2)
    })
    return columnSeries
  }

  const createColumnTemplate = (columnSeries) => {
    const columnTemplate = columnSeries.columns.template
    columnTemplate.height = am4core.percent(50)
    columnTemplate.maxHeight = 20
    columnTemplate.column.cornerRadius(60, 10, 60, 10)
    columnTemplate.strokeOpacity = 0
    columnSeries.heatRules.push({
      target: columnTemplate,
      property: `fill`,
      dataField: `valueX`,
    })
    columnSeries.mainContainer.mask = undefined
    return columnTemplate
  }

  const createCircleBullet = (columnTemplate) => {
    const circleBullet = columnTemplate.createChild(am4charts.CircleBullet)
    circleBullet.circle.radius = 5
    circleBullet.valign = `middle`
    circleBullet.align = `left`
    circleBullet.isMeasured = true
    circleBullet.interactionsEnabled = false
    circleBullet.horizontalCenter = `right`
    circleBullet.interactionsEnabled = false
    return circleBullet
  }

  const createCircle = (circleBullet) => {
    // let hoverState = circleBullet.states.create(`hover`)
    const outLineCircle = circleBullet.createChild(am4core.Circle)
    outLineCircle.adapter.add(`radius`, (radius, target) => {
      const circleBullet = target.parent
      return circleBullet.circle.pixelRadius + 10
    })
  }

  const cursorEvents = (chart, columnSeries) => {
    if (!chart) return
    if (!columnSeries) return
    let previousBullet = null
    chart.cursor.events.on(`cursorpositionchanged`, () => {
      const dataItem = columnSeries.tooltipDataItem || null
      if (dataItem.column) {
        let bullet = dataItem.column.children.getIndex(1)
        if (previousBullet && previousBullet !== bullet) previousBullet.isHover = false
        if (previousBullet !== bullet) {
          let hs = bullet.states.getKey(`hover`)
          if (hs?.properties?.dx ?? null) hs.properties.dx = dataItem.column.pixelWidth
          bullet.isHover = true
          previousBullet = bullet
        }
      }
    })
  }

  useEffect(() => {
    if (page !== `SIMILAR_CHART` || !trendList.length) return
    const chart = createChart()
    createCategoryAxis(chart)
    createValueAxis(chart)
    const curColumnSeries = createColumnSeries(chart)
    const columnTemplate = createColumnTemplate(curColumnSeries)
    const curCircleBullet = createCircleBullet(columnTemplate)
    createCircle(curCircleBullet)
    if (!mainTarget[perspective].key) {
      InitializeSimilarity(chart, trendList)
    } else {
      InitializeSimilarity(chart, trendList, trendSimilarityList[perspective])
    }
    cursorEvents(chart, curColumnSeries)
    chartData.current = chart
    return () => {
      disposeChart()
    }
  }, [trendList, page, locale, trendSimilarityList[perspective]])

  useEffect(() => {
    if (closing) {
      disposeChart()
    }
  }, [closing])

  const InitializeSimilarity = (chart, trendList, similarityList = []) => {
    const trendMergeList = trendList.map((trend) => {
      let similarity = 0.0
      if (similarityList.length) {
        const curSimilarity = similarityList.find((similarity) => similarity.trendNo === trend.trendNo)
        similarity = curSimilarity.similarity
      }
      return {
        ...trend,
        similarity: Math.round(similarity * 10000) / 100,
      }
    })
    const trendSortList = trendMergeList.sort((a, b) => {
      if (a.similarity > b.similarity) return 1
      if (a.similarity < b.similarity) return -1
      return 0
    })
    chart.data = trendSortList
  }
  return <div id={id} className={classes.cssGrid} />
}
